import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useVpnGatewayCalc } from '../../hooks/useCalculate'
import { useAnalytics } from '../../hooks/useAnalytics'
import type { VpnGatewaySku, VpnGatewayRequest } from '../../api/types'
import { TrustBar } from '../shared/TrustBar'
import { ShareButton } from '../shared/ShareButton'
import { FieldTooltip } from '../shared/FieldTooltip'
import { ScenarioPresets } from '../shared/ScenarioPresets'

function fmt(n: number) {
  return n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

const SKU_OPTIONS: { value: VpnGatewaySku; label: string; group: string }[] = [
  { value: 'basic',     label: 'Basic (legacy, dev/test only)',  group: 'Basic' },
  { value: 'vpngw1',   label: 'VpnGw1 — 650 Mbps',             group: 'Standard' },
  { value: 'vpngw2',   label: 'VpnGw2 — 1 Gbps',               group: 'Standard' },
  { value: 'vpngw3',   label: 'VpnGw3 — 1.25 Gbps',            group: 'Standard' },
  { value: 'vpngw4',   label: 'VpnGw4 — 5 Gbps',               group: 'Standard' },
  { value: 'vpngw5',   label: 'VpnGw5 — 10 Gbps',              group: 'Standard' },
  { value: 'vpngw1az', label: 'VpnGw1AZ — 650 Mbps (zone-redundant)', group: 'Zone-redundant (AZ)' },
  { value: 'vpngw2az', label: 'VpnGw2AZ — 1 Gbps (zone-redundant)',   group: 'Zone-redundant (AZ)' },
  { value: 'vpngw3az', label: 'VpnGw3AZ — 1.25 Gbps (zone-redundant)', group: 'Zone-redundant (AZ)' },
  { value: 'vpngw4az', label: 'VpnGw4AZ — 5 Gbps (zone-redundant)',   group: 'Zone-redundant (AZ)' },
  { value: 'vpngw5az', label: 'VpnGw5AZ — 10 Gbps (zone-redundant)',  group: 'Zone-redundant (AZ)' },
]

const AZ_SKUS = new Set(['vpngw1az', 'vpngw2az', 'vpngw3az', 'vpngw4az', 'vpngw5az'])

function NumInput({ label, value, onChange, min = 0, max, step = 1, hint, tooltipContent }: {
  label: string; value: number; onChange: (v: number) => void
  min?: number; max?: number; step?: number; hint?: string; tooltipContent?: string
}) {
  return (
    <div>
      <label className="flex items-center text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
        {label}{tooltipContent && <FieldTooltip content={tooltipContent} />}
      </label>
      {hint && <p className="text-[11px] mb-1" style={{ color: 'var(--text-tertiary)' }}>{hint}</p>}
      <input
        type="number" value={value} min={min} max={max} step={step}
        onChange={(e) => onChange(Math.max(min, Number(e.target.value)))}
        className="w-full rounded px-3 py-2 text-[13px] border"
        style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
      />
    </div>
  )
}

export default function VpnGatewayCalc() {
  const [sku, setSku] = useState<VpnGatewaySku>('vpngw1')
  const [outboundGb, setOutboundGb] = useState(0)

  const inputs: VpnGatewayRequest = { sku, monthlyOutboundGb: outboundGb }
  const { data: result, isLoading, isError } = useVpnGatewayCalc(inputs)
  const { trackCalculatorUsed } = useAnalytics()

  useEffect(() => {
    if (result?.monthly_estimate) {
      trackCalculatorUsed({ calculator: 'vpn-gateway', result_gbp: result.monthly_estimate, tier: sku })
    }
  }, [result?.monthly_estimate])

  const isAz = AZ_SKUS.has(sku)

  // Group options for the select
  const groups = ['Basic', 'Standard', 'Zone-redundant (AZ)']

  return (
    <div className="space-y-6">
      <TrustBar />

      <ScenarioPresets presets={[
        {
          label: 'Basic (dev / PoC)',
          description: 'Basic SKU · dev/test only — cheapest option, no production SLA',
          apply: () => { setSku('basic'); setOutboundGb(0); },
        },
        {
          label: 'Production (VpnGw1)',
          description: 'VpnGw1 · 650 Mbps · 500 GB/mo — most common enterprise site-to-site',
          apply: () => { setSku('vpngw1'); setOutboundGb(500); },
        },
        {
          label: 'Zone-redundant (VpnGw1AZ)',
          description: 'VpnGw1AZ · 650 Mbps · zone-resilient — shows AZ cost premium',
          apply: () => { setSku('vpngw1az'); setOutboundGb(500); },
        },
      ]} />

      {/* SKU + data inputs */}
      <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Gateway configuration</h2>

        <div>
          <label className="flex items-center text-[13px] font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            SKU
            <FieldTooltip content="Basic is legacy dev/test only. VpnGw1–5 scale by throughput. AZ variants add zone-redundancy at ~10% premium — only needed for production workloads with a documented availability requirement." />
          </label>
          <select
            value={sku}
            onChange={(e) => setSku(e.target.value as VpnGatewaySku)}
            className="w-full rounded px-3 py-2 text-[13px] border"
            style={{ backgroundColor: 'var(--surface-container)', borderColor: 'var(--outline-variant)', color: 'var(--text-primary)' }}
          >
            {groups.map(group => (
              <optgroup key={group} label={group}>
                {SKU_OPTIONS.filter(o => o.group === group).map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>

        <NumInput
          label="Monthly outbound data (GB)"
          value={outboundGb}
          onChange={setOutboundGb}
          min={0}
          max={1000000}
          step={100}
          hint="First 100 GB free. Inbound data is always free."
          tooltipContent="VPN Gateway egress is billed at standard Azure internet egress rates. Site-to-site replication traffic (inbound to Azure) is free."
        />
      </div>

      {/* AZ warning */}
      {isAz && (
        <div className="p-4 rounded-lg flex gap-3" style={{ backgroundColor: 'rgba(232,176,0,0.08)', border: '1px solid rgba(232,176,0,0.3)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[18px] shrink-0 mt-0.5">info</span>
          <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            Zone-redundant SKUs cost ~10% more than the equivalent non-AZ SKU. Confirm you need zone redundancy — typically only production workloads with a documented availability requirement justify the premium.
          </p>
        </div>
      )}

      {sku === 'basic' && (
        <div className="p-4 rounded-lg flex gap-3" style={{ backgroundColor: 'rgba(164,38,44,0.08)', border: '1px solid rgba(164,38,44,0.3)' }}>
          <span className="material-symbols-outlined text-[#a4262c] text-[18px] shrink-0 mt-0.5">warning</span>
          <p className="text-[13px]" style={{ color: 'var(--text-secondary)' }}>
            Basic is a legacy SKU for dev/test only. Maximum 100 Mbps throughput, no SLA, and no support for BGP, active-active configuration, or custom IPsec policies.
          </p>
        </div>
      )}

      {/* Result panel */}
      {isLoading && (
        <div className="p-5 rounded-lg text-center text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: 'var(--text-tertiary)' }}>
          Calculating…
        </div>
      )}
      {isError && (
        <div className="p-5 rounded-lg text-[13px]" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)', color: '#a4262c' }}>
          Could not fetch pricing. Check your inputs and try again.
        </div>
      )}
      {result && !isLoading && (
        <div className="p-5 rounded-lg space-y-4" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
          <div className="flex items-baseline justify-between flex-wrap gap-2">
            <h2 className="text-[14px] font-semibold" style={{ color: 'var(--text-primary)' }}>Estimated monthly cost</h2>
            <span className="text-[28px] md:text-[36px] font-bold" style={{ color: '#0078d4' }}>£{fmt(result.monthly_estimate)}</span>
          </div>

          <div className="space-y-2 pt-2" style={{ borderTop: '1px solid var(--outline-variant)' }}>
            {result.breakdown?.map((item: { label: string; value: number; unit?: string; isFree?: boolean }) => (
              <div key={item.label} className="flex justify-between text-[13px] gap-2">
                <span style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                <span style={{ color: item.isFree ? 'var(--text-tertiary)' : 'var(--text-primary)' }}>
                  {item.isFree ? 'Free' : `£${fmt(item.value)}`}
                </span>
              </div>
            ))}
          </div>

          {/* AZ vs non-AZ comparison */}
          {result.az_comparison && (
            <div className="p-3 rounded text-[13px] space-y-1" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="font-semibold" style={{ color: 'var(--text-primary)' }}>Zone-redundancy premium</p>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>Equivalent non-AZ ({result.az_comparison.sku.toUpperCase()})</span>
                <span style={{ color: 'var(--text-primary)' }}>£{fmt(result.az_comparison.monthly)}/mo</span>
              </div>
              <div className="flex justify-between">
                <span style={{ color: 'var(--text-secondary)' }}>AZ premium</span>
                <span style={{ color: '#e8b000' }}>+£{fmt(result.az_comparison.az_premium_monthly)}/mo</span>
              </div>
            </div>
          )}

          {result.warnings?.map((w: string) => (
            <div key={w} className="flex gap-2 text-[12px]" style={{ color: 'var(--text-tertiary)' }}>
              <span className="material-symbols-outlined text-[14px] shrink-0 mt-0.5">info</span>
              <span>{w}</span>
            </div>
          ))}

          <div className="flex gap-2 pt-2">
            <ShareButton inputs={inputs} result={result} service="vpn-gateway" />
          </div>

          <a href="https://azure.microsoft.com/en-gb/pricing/details/vpn-gateway/" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-[10px] font-mono mt-1" style={{ color: 'var(--text-tertiary)' }}>
            <span className="material-symbols-outlined text-[13px]">open_in_new</span>
            Verify on official Azure pricing page
          </a>

          <p className="text-[11px]" style={{ color: 'var(--text-tertiary)' }}>{result.disclaimer}</p>
        </div>
      )}

      {/* Billing traps */}
      <div className="p-5 rounded-lg" style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}>
        <h2 className="text-[14px] font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
          <span className="material-symbols-outlined text-[#e8b000] text-[16px] align-middle mr-1">warning</span>
          VPN Gateway billing traps
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {[
            {
              title: 'AZ premium on every SKU',
              body: 'VpnGw1AZ costs ~10% more than VpnGw1 every month. If zone redundancy is not in your SLA, you are paying for insurance you have not committed to using.',
            },
            {
              title: 'Per-connection charges',
              body: 'S2S tunnels (£0.0113/hr each) and P2S user connections (£0.0075/hr each) add to the gateway flat fee. High tunnel counts on Basic tier can exceed the flat fee.',
            },
            {
              title: 'Basic is not upgradeable',
              body: 'Migrating from Basic to any other SKU requires deleting and recreating the gateway — a several-hour outage for your VPN connections.',
            },
            {
              title: 'Egress is billed separately',
              body: 'Data sent from Azure over the VPN is billed at standard internet egress rates (first 100 GB free, then ~£0.0376/GB). High-bandwidth replication or backup workloads add up quickly.',
            },
          ].map(trap => (
            <div key={trap.title} className="p-3 rounded" style={{ backgroundColor: 'var(--bg-surface)', border: '1px solid var(--outline-variant)' }}>
              <p className="text-[13px] font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{trap.title}</p>
              <p className="text-[12px]" style={{ color: 'var(--text-secondary)' }}>{trap.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Cross-links */}
      <div className="space-y-2">
        <Link
          to="/bastion"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>screen_share</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Azure Bastion calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>VPN Gateway is the alternative when you need network-level access for many users</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
        <Link
          to="/networking"
          className="flex items-center gap-3 p-4 rounded transition-colors group"
          style={{ backgroundColor: 'var(--surface-container)', border: '1px solid var(--outline-variant)' }}
        >
          <span className="material-symbols-outlined text-[#0078d4] text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>hub</span>
          <div>
            <p className="text-[14px] font-semibold group-hover:text-[#0078d4] transition-colors" style={{ color: 'var(--text-secondary)' }}>Networking calculator</p>
            <p className="text-[12px]" style={{ color: 'var(--text-tertiary)' }}>Public IPs, NAT Gateway, DDoS Protection, and NSG flow logs</p>
          </div>
          <span className="material-symbols-outlined text-[#0078d4] text-[16px] ml-auto">arrow_forward</span>
        </Link>
      </div>
    </div>
  )
}
